import { useEffect, useRef, useState, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Form, Input, Button, Checkbox, Spin, Modal } from "antd";
import { openConfirmNotification, openErrorNotification } from "../utils/toast";
import AuthLayout from "./AuthLayout";
// import "../styles/login.css";
import useOemData from "../hooks/useOemData";
import usePostLogin from "../hooks/usePostLogin";
import { LoadingOutlined } from "@ant-design/icons";
import { ResetPassword } from "./ResetPassword";
import Signup from "./Signup";
import LoginProfileIcon from "../../assets/images/vectors/LoginProfileIcon";
import PasswordIcon from "../../assets/images/vectors/PasswordIcon";
import { useAuthData } from "../../providers/AuthProvider";
import { decodeSSOPayload, SSO_PAYLOAD_PARAM } from "../../utils/ssoTransfer";
import axios from "axios";
import SocialLoginButtons from "../components/SocialLoginButtons";
import {
  clearAllAuth0States,
  clearStoredAuth0State,
  getSSOService,
  getStoredAuth0State,
  SSO_PROVIDER_CODE_KEY,
  SSO_PROVIDER_STORAGE_KEY,
} from "../../utils/ssoService";
import { getUserinfoUrl, ssoConfig } from "../utils/ssoConfig";

function getApiBase() {
  const raw = process.env.REACT_APP_API_URL;
  if (raw == null) return "";
  const s = String(raw).trim();
  if (!s || s === "undefined" || s === "null" || s === "''" || s === '""') {
    return "";
  }
  return s.replace(/\/+$/, "");
}

function apiUrl(path) {
  const p = path.startsWith("/") ? path : `/${path}`;
  const base = getApiBase();
  return base ? `${base}${p}` : p;
}

const Login = () => {
  const { setUserData } = useAuthData();
  const location = useLocation();
  const [form] = Form.useForm();
  const postUrl = apiUrl("/api/accounts/auth/login/");
  const securityCodeUrl = apiUrl("/api/accounts/auth/verify/");
  const [isModalOpen, setIsModalOpen] = useState();
  const [isSecondModalOpen, setIsSecondModalOpen] = useState();
  const [registerModal, setRegisterModal] = useState(false);
  const [codePage, setCodePage] = useState(false);
  const { oemNo, oemData, loading: oemDataLoading } = useOemData();
  const [floading, setFloading] = useState(false);

  // SSO state
  const [loadingProvider, setLoadingProvider] = useState(null);
  const loadingProviderRef = useRef(null);
  const lastProviderRef = useRef(null);
  const processedStateIdsRef = useRef(new Set());
  const pendingExchangeStateIdRef = useRef(null);
  const exchangeInFlightRef = useRef(false);
  const popupMonitorRef = useRef(null);

  // Extract the token from the URL
  const queryParams = new URLSearchParams(location.search);
  const token = queryParams.get("token");

  localStorage.setItem("oemNo", oemNo);
  oemData && localStorage.setItem("oemData", JSON.stringify(oemData));
  const {
    data: postData,
    loading: postLoading,
    error: postError,
    postData: postAction,
  } = usePostLogin(postUrl);

  const {
    data: codeData,
    loading: codeLoading,
    error: codeError,
    postData: sendCode,
  } = usePostLogin(securityCodeUrl);

  const navigate = useNavigate();

  // ─── SSO helpers ────────────────────────────────────────────────────────────

  const clearStoredProvider = useCallback(() => {
    loadingProviderRef.current = null;
    lastProviderRef.current = null;
    try {
      sessionStorage.removeItem(SSO_PROVIDER_STORAGE_KEY);
    } catch (error) {
      console.warn("Failed to clear stored SSO provider", error);
    }
  }, []);

  useEffect(() => {
    loadingProviderRef.current = loadingProvider;
    if (loadingProvider) {
      lastProviderRef.current = loadingProvider;
    } else if (!exchangeInFlightRef.current) {
      clearStoredProvider();
    }
  }, [clearStoredProvider, loadingProvider]);

  // Handle successful SSO login
  const handleLoginSuccess = useCallback(
    (data) => {
      const { role_value } = data;
      sessionStorage.setItem("userData", JSON.stringify(data));
      setUserData(data);
      openConfirmNotification("Success");
      if ([1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].includes(role_value)) {
        navigate("/site");
      } else {
        openErrorNotification("Username or password is incorrect.");
        navigate("/");
      }
    },
    [navigate, setUserData],
  );

  // SSO token exchange with backend
  const handleSSOExchange = useCallback(
    async ({ access_token, userinfo_url, auth_provider }) => {
      try {
        const apiGatewayUrl = process.env.REACT_APP_GATEWAY_URL;
        const response = await axios.post(apiUrl("/api/oauth2/exchange/"), {
          access_token,
          userinfo_url,
          auth_provider,
        });

        const data = response?.data?.data ?? response?.data;
        handleLoginSuccess(data);
        setLoadingProvider(null);
        clearStoredProvider();
        exchangeInFlightRef.current = false;

        if (pendingExchangeStateIdRef.current) {
          clearStoredAuth0State(pendingExchangeStateIdRef.current);
          pendingExchangeStateIdRef.current = null;
        }

        // Redeem code if present
        const code = localStorage.getItem(SSO_PROVIDER_CODE_KEY);
        if (code) {
          try {
            await axios.post(`${apiGatewayUrl}/api/sso-redeem`, { code });
          } catch (e) {
            console.error("SSO redeem failed:", e);
          } finally {
            localStorage.removeItem(SSO_PROVIDER_CODE_KEY);
            localStorage.removeItem(SSO_PROVIDER_STORAGE_KEY);
            clearAllAuth0States();
          }
        }
      } catch (error) {
        console.error("SSO exchange failed:", error);
        openErrorNotification(
          error?.response?.data?.message || "SSO authentication failed",
        );
        setLoadingProvider(null);
        clearStoredProvider();
        exchangeInFlightRef.current = false;
        if (pendingExchangeStateIdRef.current) {
          clearStoredAuth0State(pendingExchangeStateIdRef.current);
          pendingExchangeStateIdRef.current = null;
        }
        localStorage.removeItem(SSO_PROVIDER_CODE_KEY);
        localStorage.removeItem(SSO_PROVIDER_STORAGE_KEY);
        clearAllAuth0States();
      }
    },
    [clearStoredProvider, handleLoginSuccess],
  );

  // Handle SSO payload delivered via URL query param (callback redirect flow)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const payload = params.get(SSO_PAYLOAD_PARAM);
    if (!payload) return;

    let loginPayload = null;
    try {
      loginPayload = decodeSSOPayload(payload);
    } catch (error) {
      console.error("Failed to decode SSO payload", error);
    }

    params.delete(SSO_PAYLOAD_PARAM);
    const cleanedSearch = params.toString();
    const nextUrl = `${location.pathname}${cleanedSearch ? `?${cleanedSearch}` : ""}${location.hash}`;
    navigate(nextUrl, { replace: true });

    if (loginPayload) {
      handleLoginSuccess(loginPayload);
    }
  }, [
    handleLoginSuccess,
    location.hash,
    location.pathname,
    location.search,
    navigate,
  ]);

  // Listen for SSO popup messages
  useEffect(() => {
    const allowedOrigins = new Set();
    allowedOrigins.add(window.location.origin);

    if (ssoConfig.redirectUri) {
      try {
        allowedOrigins.add(new URL(ssoConfig.redirectUri).origin);
      } catch (error) {
        console.error("Invalid SSO redirect URI configured:", error);
      }
    }

    const handleSSOPopupMessage = (event) => {
      if (!event.data || !allowedOrigins.has(event.origin)) return;

      const { type, data, error, accessToken, stateId, provider } = event.data;

      if (stateId && processedStateIdsRef.current.has(stateId)) return;

      if (type === "AUTH_SUCCESS") {
        window.dispatchEvent(
          new CustomEvent("sso-token-received", { detail: "success" }),
        );
        setLoadingProvider(null);
        if (stateId) {
          clearStoredAuth0State(stateId);
          processedStateIdsRef.current.add(stateId);
        }
        clearStoredProvider();
        handleLoginSuccess(data);
      } else if (type === "AUTH_ERROR") {
        window.dispatchEvent(
          new CustomEvent("sso-token-received", { detail: "error" }),
        );
        console.error("SSO popup error:", error);
        openErrorNotification(
          typeof error === "string" ? error : "SSO authentication failed",
        );
        setLoadingProvider(null);
        if (stateId) {
          clearStoredAuth0State(stateId);
          processedStateIdsRef.current.add(stateId);
        }
        clearStoredProvider();
        exchangeInFlightRef.current = false;
      } else if (type === "AUTH_TOKEN") {
        if (!accessToken) {
          window.dispatchEvent(
            new CustomEvent("sso-token-received", { detail: "error" }),
          );
          setLoadingProvider(null);
          if (stateId) {
            clearStoredAuth0State(stateId);
            processedStateIdsRef.current.add(stateId);
          }
          exchangeInFlightRef.current = false;
          return;
        }

        const storedState = stateId ? getStoredAuth0State(stateId) : null;
        let storedProviderFallback = null;
        try {
          const storedValue = sessionStorage.getItem(SSO_PROVIDER_STORAGE_KEY);
          if (["google", "microsoft", "apple"].includes(storedValue)) {
            storedProviderFallback = storedValue;
          }
        } catch (e) {
          console.warn("Failed to read stored SSO provider", e);
        }

        const resolvedProvider =
          provider ||
          storedState?.provider ||
          loadingProvider ||
          lastProviderRef.current ||
          storedProviderFallback ||
          undefined;

        const exchangeProvider =
          resolvedProvider ?? storedProviderFallback ?? lastProviderRef.current;
        if (
          exchangeProvider &&
          loadingProviderRef.current !== exchangeProvider
        ) {
          setLoadingProvider(exchangeProvider);
        }

        if (stateId) {
          processedStateIdsRef.current.add(stateId);
        } else if (exchangeInFlightRef.current) {
          return;
        }

        pendingExchangeStateIdRef.current = stateId ?? null;
        exchangeInFlightRef.current = true;

        window.dispatchEvent(
          new CustomEvent("sso-token-received", { detail: "token" }),
        );
        handleSSOExchange({
          access_token: accessToken,
          userinfo_url: getUserinfoUrl(),
          auth_provider:
            resolvedProvider ?? lastProviderRef.current ?? undefined,
        });
      }
    };

    window.addEventListener("message", handleSSOPopupMessage);

    let broadcastChannel = null;
    try {
      broadcastChannel = new BroadcastChannel("sso-auth");
      broadcastChannel.onmessage = (channelEvent) => {
        handleSSOPopupMessage({
          data: channelEvent.data,
          origin: window.location.origin,
          source: null,
        });
      };
    } catch (error) {
      console.warn("BroadcastChannel not available", error);
    }

    return () => {
      window.removeEventListener("message", handleSSOPopupMessage);
      if (broadcastChannel) broadcastChannel.close();
    };
  }, [
    clearStoredProvider,
    handleLoginSuccess,
    handleSSOExchange,
    loadingProvider,
  ]);

  // Monitor popup closed without completing auth
  useEffect(() => {
    let interactionEnded = false;
    const handleTokenReceived = () => {
      interactionEnded = true;
    };

    if (loadingProvider) {
      if (popupMonitorRef.current !== null) {
        window.clearInterval(popupMonitorRef.current);
      }
      let popupSeenOpen = false;
      const popupOpenedAt = Date.now();

      window.addEventListener("sso-token-received", handleTokenReceived);
      popupMonitorRef.current = window.setInterval(() => {
        const popup = window.__ssoPopup;
        if (!popup) return;
        if (!popup.closed) {
          popupSeenOpen = true;
          return;
        }

        if (popupSeenOpen) {
          const elapsed = Date.now() - popupOpenedAt;
          if (elapsed < 500) return;
          if (popupMonitorRef.current !== null) {
            window.clearInterval(popupMonitorRef.current);
            popupMonitorRef.current = null;
          }
          window.removeEventListener("sso-token-received", handleTokenReceived);
          if (
            loadingProviderRef.current &&
            !exchangeInFlightRef.current &&
            !interactionEnded
          ) {
            setLoadingProvider(null);
            clearStoredProvider();
          }
        }
      }, 500);
    } else {
      if (popupMonitorRef.current !== null) {
        window.clearInterval(popupMonitorRef.current);
        popupMonitorRef.current = null;
      }
      window.removeEventListener("sso-token-received", handleTokenReceived);
      if (!exchangeInFlightRef.current && !interactionEnded) {
        clearStoredProvider();
      }
    }

    return () => {
      if (popupMonitorRef.current !== null) {
        window.clearInterval(popupMonitorRef.current);
        popupMonitorRef.current = null;
      }
      window.removeEventListener("sso-token-received", handleTokenReceived);
    };
  }, [clearStoredProvider, loadingProvider]);

  const handleSSOLogin = async (connection, provider) => {
    const ssoService = getSSOService();
    if (!ssoService) {
      openErrorNotification("SSO is not configured properly");
      return;
    }

    setLoadingProvider(provider);
    try {
      sessionStorage.setItem(SSO_PROVIDER_STORAGE_KEY, provider);
    } catch (error) {
      console.warn("Failed to persist SSO provider selection", error);
    }

    processedStateIdsRef.current.clear();
    pendingExchangeStateIdRef.current = null;
    exchangeInFlightRef.current = false;

    try {
      await ssoService.initiateLogin(connection, provider);
    } catch (error) {
      openErrorNotification(error?.message || "Failed to initiate SSO login");
      setLoadingProvider(null);
      clearStoredProvider();
    }
  };

  const handleGoogleLogin = () => handleSSOLogin("google-oauth2", "google");
  const handleMicrosoftLogin = () => handleSSOLogin("windowslive", "microsoft");
  const handleAppleLogin = () => handleSSOLogin("apple", "apple");

  //Forget password functionalities
  const handleForgetClick = async () => {
    try {
      // Get the userId field value from the form
      const userId = form.getFieldValue("userId");
      if (!userId || userId.trim() === "") {
        openErrorNotification(" Enter your username or email   first!");
        return;
      }
      // if (!userId) {
      //   showToast("Please enter your email or username!", "error", faWarning);
      //   return;
      // }

      setFloading(true);
      const postUrl = apiUrl("/api/accounts/send-email/");

      // Send POST request
      const response = await fetch(postUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ Email: userId }),
      });

      // Check if the response is OK
      if (!response.ok) {
        throw new Error("Failed to send email. Please try again.");
      }

      const data = await response.json();

      if (data?.message) {
        openConfirmNotification("Success");
        setIsModalOpen(true);
      } else {
        openErrorNotification(data?.Email || "Something went wrong");
      }
    } catch (error) {
      console.log(error);
      openErrorNotification(String(error) || "Something went wrong");
    }
    setFloading(false);
  };

  useEffect(() => {
    if (postData) {
      const { role_value, two_factor } = postData;
      if (two_factor) {
        setCodePage(true);
        setIsSecondModalOpen(true);
        // sessionStorage.setItem("userData", JSON.stringify(postData));
        openConfirmNotification("Success");
      } else {
        sessionStorage.setItem("userData", JSON.stringify(postData));
        setUserData(postData);
        openConfirmNotification("Success");
        if (role_value === 1 || role_value === 2 || role_value === 3) {
          navigate("/site");
        } else if ([4, 5, 6, 7, 8, 9].includes(role_value)) {
          navigate("/site");
        } else if ([10, 11, 12].includes(role_value)) {
          navigate("/site");
        } else {
          openErrorNotification("Username or password is incorrect.");
          navigate("/");
        }
      }
    }
  }, [postData]);

  useEffect(() => {
    if (postError) {
      openErrorNotification(
        postError?.response?.data?.message || "Something went wrong",
      );
    }
  }, [postError]);

  useEffect(() => {
    if (codeData) {
      setCodePage(true);
      sessionStorage.setItem("userData", JSON.stringify(codeData));
      setUserData(codeData);

      openConfirmNotification("Success");

      const { role_value } = codeData;
      if ([1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].includes(role_value)) {
        navigate("/site");
      }
      // if (role_value === 1 || role_value === 2 || role_value === 3) {
      // } else if ([4, 5, 6, 7, 8, 9, 10, 11, 12].includes(role_value)) {
      //   navigate("/order");
      // } else {
      //   openErrorNotification("  Username or password is incorrect.");
      //   navigate("/");
      // }
    }
    if (codeError) {
      openErrorNotification(
        codeError?.response?.data?.message || "Something went wrong",
      );
    }
  }, [codeData, codeError]);

  const sendLocalTime = async () => {
    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const timeStamp = Math.floor(Date.now() / 1000);

    const url = apiUrl("/api/accounts/time/");
    const response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        timestamp: timeStamp, // Send as ISO string
        timezone: timeZone,
      }),
    });

    const data = await response.json();
  };

  useEffect(() => {
    if (token) setRegisterModal(true);
    sendLocalTime();
  }, []);

  // on submit handler.
  const onSubmitHandler = async (values) => {
    try {
      await postAction(values);
    } catch (error) {
      openErrorNotification("  Username or password is incorrect.");
    }
  };

  // on submit handler.
  const onSubmitCodeHandler = async (values) => {
    try {
      const modifiedValues = {
        email: postData?.email,
        securityCode: values?.code,
      };
      await sendCode(modifiedValues);
    } catch (error) {
      openErrorNotification(
        "Login failed. Please check your security code and try again.",
      );
    }
  };

  return (
    <AuthLayout>
      <div className="rounded rounded-lg  py-12 px-8 flex flex-col items-center !bg-white">
        <div className="w-full max-w-md flex justify-center ">
          <img
            // src={`oem/${oemNo}/images/mainLogo.png`}
            // src={require(`../../oem/${oemNo}/images/mainLogo.png`)}
            src={require(`../../oem/${oemNo}/images/mainLogo.png`)}
            alt={oemData?.name}
            className="w-[200px]  mx-auto"
          />
        </div>

        <>
          <p className="my-5 text-2xl text-center"></p>
          <Form
            form={form}
            autoComplete="off"
            requiredMark={false}
            layout="vertical"
            onFinish={onSubmitHandler}
            className="w-full max-w-md"
          >
            <div className="flex flex-col gap-y-3">
              <Form.Item
                name="userId"
                className="my-input"
                rules={[
                  {
                    required: true,
                    message: "Username (Email) is required.",
                  },
                ]}
              >
                <Input
                  className="h-[38px] !bg-[#EEEEEF]"
                  placeholder="Username (Email)"
                  prefix={<LoginProfileIcon className="mr-2" />}
                  style={{ width: "292.92px" }}
                />
              </Form.Item>

              <Form.Item
                // label="Password"
                name="password"
                className="my-input"
                rules={[
                  {
                    required: true,
                    message: "Password is required",
                  },
                ]}
              >
                <Input.Password
                  className="!h-[38px] !bg-[#EEEEEF] "
                  placeholder="Password"
                  prefix={<PasswordIcon className="mr-2" />}
                  style={{ width: "292.92px" }}
                />
              </Form.Item>
              <div className="flex gap-x-3  items-center w-full">
                <div
                  onClick={handleForgetClick}
                  className="text-customColor text-sm font-[Poppins] cursor-pointer"
                >
                  Forgot your Password?
                </div>
                <Form.Item
                  name="remember"
                  valuePropName="checked"
                  className="!mb-0"
                >
                  <Checkbox className="text-[#374151] text-sm font-medium">
                    Remember me
                  </Checkbox>
                </Form.Item>
              </div>
              {/* <p className="text-red-600 p-2 text-[15px] text-center">
              {inputValidate}
            </p> */}
              <Form.Item className="!mb-0 px-[2px] mt-2">
                <Button
                  htmlType="submit"
                  className="w-full bg-customColor text-white hover:!bg-white hover:!text-customColor border-none font-medium text-[15px] !rounded !py-4 h-[35px]"
                >
                  {postLoading ? (
                    <Spin
                      indicator={
                        <LoadingOutlined
                          spin
                          className="h-4 w-4"
                          style={{ color: "white" }}
                        />
                      }
                    />
                  ) : (
                    "Login"
                  )}
                </Button>
              </Form.Item>
            </div>
          </Form>
        </>

        {/* SSO Section — only shown when env SSO is enabled AND the OEM account has sso_enabled=true */}

        <div className="mt-4 w-full max-w-[292.92px]">
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "12px",
              margin: "16px 0",
              fontSize: "12px",
              color: "#9ca3af",
              textTransform: "uppercase",
              fontWeight: 600,
              letterSpacing: "0.05em",
            }}
          >
            <span style={{ flex: 1, height: "1px", background: "#e5e7eb" }} />
            <span>Or</span>
            <span style={{ flex: 1, height: "1px", background: "#e5e7eb" }} />
          </div>
          <SocialLoginButtons
            onGoogleLogin={handleGoogleLogin}
            onMicrosoftLogin={handleMicrosoftLogin}
            onAppleLogin={handleAppleLogin}
            loadingProvider={loadingProvider}
          />
        </div>

        <Modal
          open={isModalOpen}
          // onCancel={() => setIsModalOpen(false)}
          footer={null}
          // width={1000}
          closable={false} // Removes the cancel (close) icon in the header
          centered
        >
          <ResetPassword setIsModalOpen={setIsModalOpen} />
        </Modal>

        <Modal
          open={registerModal}
          // onCancel={() => setRegisterModal(false)}
          closable={false}
          footer={null}
          // width={1000}
          centered
        >
          <Signup setRegisterModal={setRegisterModal} token={token} />
        </Modal>
      </div>
      {codePage && (
        <Modal
          open={isSecondModalOpen}
          // onCancel={() => setIsSecondModalOpen(false)}
          footer={null}
          closable={false}
          // width={1000}
          centered
        >
          <>
            <div className="flex justify-center py-2 items-center w-full">
              <div className="bg-white  rounded-lg w-full max-w-md p-2">
                <div className="mb-6 text-center">
                  <h3 className="text-2xl font-semiboldtext-black font-[Poppins]">
                    Two Factor Authentication
                  </h3>
                  <p className="text-sm text-black font-[Poppins] mt-2">
                    Security Code has been sent to your Email.
                    <br />
                    Enter the Security Code to authenticate your login attempt.
                  </p>
                </div>
                <Form
                  autoComplete="off"
                  requiredMark={false}
                  layout="vertical"
                  onFinish={onSubmitCodeHandler}
                  className="w-full max-w-md"
                >
                  <Form.Item
                    name="code"
                    className="my-input !text-black font-['Poppins']"
                    rules={[
                      {
                        required: true,
                        message: "Please enter your security code!",
                      },
                    ]}
                  >
                    <Input
                      className="h-[40px] !bg-white"
                      placeholder="Security Code"
                      prefix={<PasswordIcon />}
                    />
                  </Form.Item>
                  <Form.Item className="!mb-0 mt-5 ">
                    <div className="flex justify-between items-center gap-3 px-1">
                      <Button
                        htmlType="submit"
                        className="flex-1 h-[35px] text-sm font-medium bg-customColor text-white hover:!bg-white border-none hover:!text-customColor font-medium text-[14px] !rounded !py-4 h-[35px] "
                      >
                        {codeLoading ? (
                          <span>
                            <Spin
                              indicator={<LoadingOutlined spin />}
                              size="default"
                              // className="mr-2"
                            />
                            Apply
                          </span>
                        ) : (
                          "Apply"
                        )}
                      </Button>

                      <Button
                        htmlType="submit"
                        className="flex-1 h-[35px] text-[12px]  bg-[#E9E9E9] text-customColor hover:!bg-customColor hover:!text-white border-none font-medium text-[14px] !rounded !py-4 h-[35px]"
                        onClick={() => setIsSecondModalOpen(false)}
                      >
                        Cancel
                      </Button>
                    </div>
                  </Form.Item>
                </Form>
              </div>
            </div>
          </>
        </Modal>
      )}
      {floading ? (
        <div className="fixed bottom-4 left-1/2 transform -translate-x-1/2 flex items-center justify-center">
          <div className="bg-white shadow-lg rounded-lg px-4 py-2 flex items-center">
            <Spin indicator={<LoadingOutlined spin />} size="default" />
            <span className="ml-2 text-gray-700 font-['Poppins'] font-medium">
              Loading
            </span>
          </div>
        </div>
      ) : null}
    </AuthLayout>
  );
};

export default Login;
